/**
 * Author with AI API client (/api/author-with-ai). The DTO types mirror
 * author-api's src/features/authorWithAi/contracts/views.ts and messages.ts;
 * the two repos cannot share code, so the schemas below are the frontend's
 * copy, checked against backend fixtures by authorWithAiApi.contract.test.ts.
 *
 * The schemas are deliberately lenient: unknown keys are dropped and unknown
 * message part types are kept as `{ type }` so the thread can skip them.
 */

import { z } from 'zod';
import type { QuestionBrowseFacets, QuestionListResult } from '@course-authoring/shared-types';
import { buildQuestionSearchParams, type QuestionListFilters } from './questionApi';
import { fetchWithAuthRetry } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const BASE = `${API_BASE_URL}/author-with-ai`;

/** Mirrors the feature capability registry; the general Question Bank supports more types. */
export const AUTHOR_WITH_AI_QUESTION_TYPES = ['likert-scale', 'single-choice-answer', 'workplace-color-spectrum', 'categorization'] as const;
export type AuthorWithAiQuestionType = (typeof AUTHOR_WITH_AI_QUESTION_TYPES)[number];
export const isAuthorWithAiQuestionType = (type: string): type is AuthorWithAiQuestionType =>
  (AUTHOR_WITH_AI_QUESTION_TYPES as readonly string[]).includes(type);

// ── Working state ────────────────────────────────────────────────────────────

const factSchema = z.union([
  z.object({ status: z.literal('unknown') }),
  z.object({ status: z.enum(['supplied', 'assumed']), value: z.unknown(), evidenceMessageId: z.string().optional() }),
]);

export const BRIEF_FIELDS = ['topic', 'audience', 'objectives', 'shape', 'targetMinutes'] as const;
export type BriefField = (typeof BRIEF_FIELDS)[number];

const briefSchema = z.object({
  topic: factSchema,
  audience: factSchema,
  objectives: factSchema,
  shape: factSchema,
  targetMinutes: factSchema,
});

export const DECISION_KEYS = ['anchor_question', 'additional_questions', 'video'] as const;
export type DecisionKey = (typeof DECISION_KEYS)[number];

const selectionSchema = z.object({
  id: z.string(),
  kind: z.enum(['question', 'video']),
  libraryId: z.string(),
  role: z.string(),
  questionType: z.string().optional(),
  active: z.boolean(),
});

const decisionSchema = z.object({
  required: z.boolean(),
  status: z.enum(['pending', 'selected', 'skipped']),
  selectionIds: z.array(z.string()),
});

const outlineBlockSchema = z.object({
  id: z.string(),
  type: z.string(),
  purpose: z.string(),
  selectionIds: z.array(z.string()),
});
export type OutlineBlock = z.infer<typeof outlineBlockSchema>;

const outlineLessonSchema = z.object({
  id: z.string(),
  revision: z.number(),
  title: z.string(),
  objectives: z.array(z.string()),
  estimatedMinutes: z.number(),
  blocks: z.array(outlineBlockSchema),
  /** On a build from an uploaded file: the document sections (ascending numbers) the lesson draws from. */
  sourceSections: z.array(z.number()).optional(),
});
export type OutlineLesson = z.infer<typeof outlineLessonSchema>;

const outlineSchema = z.object({
  id: z.string(),
  revision: z.number(),
  title: z.string(),
  description: z.string(),
  lessons: z.array(outlineLessonSchema),
});
export type Outline = z.infer<typeof outlineSchema>;

const approvalSchema = z.object({
  id: z.string(),
  scope: z.enum(['course', 'lesson']),
  targetId: z.string(),
  targetRevision: z.number(),
  mode: z.enum(['detailed', 'delegated']),
});

/** Where an "Ask a question" build is in the question taxonomy (author-api's questionBrowsePositionSchema). */
const browsePositionSchema = z.object({
  taxonomyId: z.number().optional(),
  parentLabel: z.string().optional(),
  labelId: z.number().optional(),
});
export type BrowsePosition = z.infer<typeof browsePositionSchema>;

/** A Centerstage concept, as a concept chip and the browse action name it (author-api's conceptPositionSchema). */
const conceptPositionSchema = z.object({ conceptId: z.number() });
export type ConceptPosition = z.infer<typeof conceptPositionSchema>;

const workingStateSchema = z.object({
  brief: briefSchema,
  selections: z.array(selectionSchema),
  choices: z.object({
    decisions: z.object({
      anchor_question: decisionSchema.optional(),
      additional_questions: decisionSchema.optional(),
      video: decisionSchema.optional(),
    }),
    browsing: z.object({
      question: browsePositionSchema.optional(),
      video: z.object({ conceptId: z.number().optional() }).optional(),
    }).optional(),
  }),
  outline: outlineSchema.nullable(),
  review: z.object({
    mode: z.enum(['undecided', 'detailed', 'delegated']),
    approvals: z.array(approvalSchema),
  }),
  position: z.object({
    step: z.string(),
    activeLessonId: z.string().nullable(),
    clarificationExchanges: z.number(),
    outstanding: z.array(z.object({
      code: z.string(),
      message: z.string(),
      decision: z.enum(DECISION_KEYS).optional(),
      lessonId: z.string().optional(),
      blockId: z.string().optional(),
    })),
  }),
});

// ── Message parts ────────────────────────────────────────────────────────────

const textPartSchema = z.object({ type: z.literal('text'), text: z.string() });
const selectionChangePartSchema = z.object({
  type: z.literal('selection_change'),
  decision: z.enum(DECISION_KEYS),
  status: z.enum(['selected', 'skipped']),
  libraryIds: z.array(z.string()),
});
const approvalPartSchema = z.object({
  type: z.literal('approval'),
  scope: z.enum(['course', 'lesson']),
  targetId: z.string(),
  revision: z.number(),
  mode: z.enum(['detailed', 'delegated']),
});
const outlineReferencePartSchema = z.object({
  type: z.literal('outline_reference'),
  outlineId: z.string(),
  revision: z.number(),
  lessonId: z.string().optional(),
  lessonRevision: z.number().optional(),
});
const resultReferencePartSchema = z.object({
  type: z.literal('result_reference'),
  runId: z.string(),
  lessonIds: z.array(z.string()),
  courseId: z.string().nullable(),
});
const outlineSnapshotPartSchema = z.object({
  type: z.literal('outline_snapshot'),
  outline: outlineSchema,
  focusedLessonId: z.string().optional(),
});
const suggestionsPartSchema = z.object({ type: z.literal('suggestions'), items: z.array(z.string()) });
/** The hidden opening message of an "Ask a question" or "Lead with a video scenario" build; renders no tile. */
const entryPartSchema = z.object({ type: z.literal('entry'), source: z.string() });
export const DOCUMENT_UNITS = ['slides', 'pages', 'sections'] as const;
/** The author's uploaded file, the first message of an "Upload a file" build; renders as a file tile. */
const attachmentPartSchema = z.object({
  type: z.literal('attachment'),
  fileName: z.string(),
  sizeBytes: z.number(),
  unit: z.enum(DOCUMENT_UNITS),
  unitCount: z.number(),
});
const browseChoicePartSchema = z.object({
  type: z.literal('browse_choice'),
  label: z.string(),
  taxonomyId: z.number().optional(),
  parentLabel: z.string().optional(),
  labelId: z.number().optional(),
  /** Set instead of taxonomyId when the choice is a Centerstage concept. */
  conceptId: z.number().optional(),
});
const taxonomyOptionsPartSchema = z.object({
  type: z.literal('taxonomy_options'),
  level: z.enum(['taxonomy', 'parent', 'child']),
  levelName: z.string(),
  taxonomyId: z.number().optional(),
  parentLabel: z.string().optional(),
  options: z.array(z.object({ label: z.string(), questionCount: z.number(), position: browsePositionSchema })),
});
const questionOptionsPartSchema = z.object({
  type: z.literal('question_options'),
  labelId: z.number(),
  label: z.string(),
  taxonomyId: z.number(),
  total: z.number(),
  items: z.array(z.object({ libraryId: z.string(), text: z.string(), questionType: z.string(), usageCount: z.number() })),
});
const conceptOptionsPartSchema = z.object({
  type: z.literal('concept_options'),
  options: z.array(z.object({
    label: z.string(),
    category: z.string(),
    scenarioCount: z.number(),
    position: conceptPositionSchema,
  })),
});
const scenarioOptionsPartSchema = z.object({
  type: z.literal('scenario_options'),
  conceptId: z.number(),
  label: z.string(),
  total: z.number(),
  items: z.array(z.object({
    libraryId: z.string(),
    title: z.string(),
    description: z.string().optional(),
    durationSeconds: z.number().optional(),
    thumbnailUrl: z.string().optional(),
    /** Legacy: on builds saved before 2026-11-01, when uncaptioned videos were refused. Ignored. */
    hasCaptions: z.boolean().optional(),
  })),
});

const questionSuggestionsPartSchema = z.object({
  type: z.literal('question_suggestions'),
  items: z.array(questionOptionsPartSchema.shape.items.element.extend({ reason: z.string() })).min(1).max(3),
});
const videoSuggestionPartSchema = z.object({
  type: z.literal('video_suggestion'),
  conceptId: z.number(),
  label: z.string(),
  category: z.string(),
  item: scenarioOptionsPartSchema.shape.items.element,
  reason: z.string(),
});

const KNOWN_PART_TYPES = [
  'text', 'selection_change', 'approval', 'outline_reference', 'result_reference', 'outline_snapshot', 'suggestions',
  'question_suggestions', 'video_suggestion',
  'entry', 'attachment', 'browse_choice', 'taxonomy_options', 'question_options', 'concept_options', 'scenario_options',
] as const;

const knownPartSchema = z.discriminatedUnion('type', [
  textPartSchema,
  selectionChangePartSchema,
  approvalPartSchema,
  outlineReferencePartSchema,
  resultReferencePartSchema,
  outlineSnapshotPartSchema,
  suggestionsPartSchema,
  entryPartSchema,
  attachmentPartSchema,
  browseChoicePartSchema,
  taxonomyOptionsPartSchema,
  questionOptionsPartSchema,
  conceptOptionsPartSchema,
  scenarioOptionsPartSchema,
  questionSuggestionsPartSchema,
  videoSuggestionPartSchema,
]);

export interface UnknownPart {
  type: string;
  unknown: true;
}

/** A part type this client does not know is kept as a marker and skipped when rendering. */
const partSchema = z.unknown().transform((value, ctx): z.infer<typeof knownPartSchema> | UnknownPart => {
  const type = typeof value === 'object' && value !== null ? (value as { type?: unknown }).type : undefined;
  if (typeof type !== 'string') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Message part without a type' });
    return z.NEVER;
  }
  if (!(KNOWN_PART_TYPES as readonly string[]).includes(type)) return { type, unknown: true };
  const parsed = knownPartSchema.safeParse(value);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) ctx.addIssue(issue);
    return z.NEVER;
  }
  return parsed.data;
});
export type MessagePart = z.infer<typeof partSchema>;
export type KnownMessagePart = z.infer<typeof knownPartSchema>;
export type PartOf<T extends KnownMessagePart['type']> = Extract<KnownMessagePart, { type: T }>;
export type OutlineSnapshotPart = PartOf<'outline_snapshot'>;
export type TaxonomyOptionsPart = PartOf<'taxonomy_options'>;
export type QuestionOptionsPart = PartOf<'question_options'>;
export type ConceptOptionsPart = PartOf<'concept_options'>;
export type ScenarioOptionsPart = PartOf<'scenario_options'>;
export type QuestionSuggestionsPart = PartOf<'question_suggestions'>;

export const isPart = <T extends KnownMessagePart['type']>(part: MessagePart, type: T): part is PartOf<T> =>
  part.type === type && !('unknown' in part);

// ── Views ────────────────────────────────────────────────────────────────────

const sourceSchema = z.union([
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('question'), libraryId: z.string() }),
  z.object({ kind: z.literal('question_browse') }),
  z.object({ kind: z.literal('video'), libraryId: z.string() }),
  z.object({ kind: z.literal('video_browse') }),
  z.object({
    kind: z.literal('uploaded_file'),
    fileId: z.string(),
    fileName: z.string(),
    documentType: z.string(),
    sizeBytes: z.number(),
    unit: z.enum(DOCUMENT_UNITS),
    unitCount: z.number(),
  }),
  z.object({ kind: z.string() }),
]);
export type BuildSource = z.infer<typeof sourceSchema>;
export type UploadedFileSource = Extract<BuildSource, { kind: 'uploaded_file' }>;
/** Narrows past the catch-all source member, which also carries kind as a string. */
export const isUploadedFileSource = (source: BuildSource): source is UploadedFileSource => source.kind === 'uploaded_file' && 'fileId' in source;

export const BUILD_STATUSES = ['planning', 'building', 'completed', 'failed', 'discarded'] as const;
export type BuildStatus = (typeof BUILD_STATUSES)[number];

const messageViewSchema = z.object({
  id: z.string(),
  sequence: z.number(),
  role: z.enum(['author', 'assistant']),
  parts: z.array(partSchema),
  runId: z.string().nullable(),
  createdAt: z.string(),
});
export type MessageView = z.infer<typeof messageViewSchema>;

const runSummarySchema = z.object({
  id: z.string(),
  kind: z.enum(['conversation', 'generation']),
  status: z.enum(['pending', 'running', 'succeeded', 'failed', 'needs_input', 'discarded']),
  attempt: z.number(),
  automaticAttempts: z.number(),
  nextAttemptAt: z.string().nullable(),
  stage: z.string().nullable(),
  error: z.object({
    code: z.string(),
    message: z.string(),
    retryable: z.boolean(),
    stage: z.string().optional(),
    operationId: z.string().optional(),
    recovery: z.array(z.string()),
  }).nullable(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type RunSummary = z.infer<typeof runSummarySchema>;

const progressStatus = z.enum(['pending', 'running', 'ready', 'failed']);

const progressSchema = z.object({
  runId: z.string(),
  stage: z.string(),
  lessons: z.array(z.object({
    outlineLessonId: z.string(),
    title: z.string(),
    status: progressStatus,
    stage: z.string().nullable(),
    lessonId: z.string().nullable(),
    editorPath: z.string().nullable(),
  })),
  course: z.object({
    status: progressStatus,
    courseId: z.string().nullable(),
    editorPath: z.string().nullable(),
  }).nullable(),
});
export type ProgressView = z.infer<typeof progressSchema>;

const resultSchema = z.object({
  runId: z.string(),
  current: z.boolean(),
  lessons: z.array(z.object({
    outlineLessonId: z.string(),
    lessonId: z.string(),
    displayId: z.string(),
    ready: z.boolean(),
    editorPath: z.string(),
  })),
  course: z.object({
    courseId: z.string(),
    displayId: z.string(),
    ready: z.boolean(),
    editorPath: z.string(),
  }).nullable(),
  completedAt: z.string().nullable(),
});
export type ResultView = z.infer<typeof resultSchema>;

const allowedActionSchema = z.object({
  type: z.string(),
  runId: z.string().optional(),
  replyOnly: z.boolean().optional(),
});

const buildViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  ownerUserId: z.number(),
  accountId: z.number().nullable(),
  source: sourceSchema,
  status: z.enum(BUILD_STATUSES),
  workingState: workingStateSchema,
  coverage: z.object({
    mode: z.enum(['detailed', 'delegated']).nullable(),
    complete: z.boolean(),
    missing: z.array(z.object({ code: z.string(), message: z.string(), targetId: z.string().optional() })),
  }),
  messages: z.array(messageViewSchema),
  nextMessageCursor: z.string().nullable(),
  execution: z.object({
    active: runSummarySchema.nullable(),
    recent: z.array(runSummarySchema),
  }),
  progress: progressSchema.nullable(),
  results: z.array(resultSchema),
  allowedActions: z.array(allowedActionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BuildView = z.infer<typeof buildViewSchema>;

const buildListItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  accountId: z.number().nullable(),
  source: sourceSchema,
  status: z.enum(BUILD_STATUSES),
  activeRun: z.object({ id: z.string(), kind: z.string(), status: z.string() }).nullable(),
  progress: z.object({ ready: z.number(), total: z.number() }).nullable(),
  output: z.object({
    kind: z.enum(['lesson', 'course']),
    publicId: z.string(),
    displayId: z.string(),
    editorPath: z.string(),
    publishState: z.enum(['draft', 'published']),
  }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BuildListItem = z.infer<typeof buildListItemSchema>;

const buildListSchema = z.object({
  items: z.array(buildListItemSchema),
  nextCursor: z.string().nullable(),
});
export type BuildList = z.infer<typeof buildListSchema>;

const admissionResponseSchema = z.object({
  buildId: z.string(),
  messageId: z.string().nullable(),
  runId: z.string().nullable(),
  operationId: z.string(),
  statusUrl: z.string(),
  replayed: z.boolean(),
  build: buildViewSchema.optional(),
});
export type AdmissionResponse = z.infer<typeof admissionResponseSchema>;

const diagnosticsSchema = z.object({
  enabled: z.boolean(),
  admissionsEnabled: z.boolean(),
  ready: z.boolean(),
  components: z.array(z.object({
    component: z.string(),
    ok: z.boolean(),
    missing: z.array(z.string()),
    required: z.boolean(),
  })),
  /** Whether "Upload a file" is set up (its private bucket); older servers leave it out. */
  uploads: z.object({ ready: z.boolean(), missing: z.array(z.string()) }).optional(),
});
export type AuthorWithAiDiagnostics = z.infer<typeof diagnosticsSchema>;

/** Runtime guards, exported for the contract test and for cache writes. */
export const authorWithAiGuards = {
  buildView: buildViewSchema,
  buildList: buildListSchema,
  admission: admissionResponseSchema,
  diagnostics: diagnosticsSchema,
};

// ── Requests ─────────────────────────────────────────────────────────────────

export type CreateBuildSource =
  | { kind: 'none' }
  | { kind: 'question'; libraryId: string }
  | { kind: 'question_browse' }
  | { kind: 'video'; libraryId: string }
  | { kind: 'video_browse' }
  | { kind: 'uploaded_file'; fileId: string };

export interface DocumentSectionInput {
  number: number;
  text: string;
}

export type BuildAction =
  | { type: 'select'; decision: DecisionKey; libraryIds: string[] }
  | { type: 'skip'; decision: DecisionKey }
  | { type: 'approve'; scope: 'course' | 'lesson'; targetId: string; revision: number; mode: 'detailed' | 'delegated' }
  | { type: 'browse'; position: BrowsePosition | ConceptPosition }
  | { type: 'start' }
  | { type: 'retry'; runId: string; replyOnly?: boolean }
  | { type: 'revise'; runId: string }
  | { type: 'discard' };

export interface CreateBuildInput {
  accountId: number | null;
  source: CreateBuildSource;
  initialMessage?: string;
  /** The extracted sections of an uploaded file; sent with an uploaded_file source only. */
  document?: { sections: DocumentSectionInput[] };
}

// ── Errors ───────────────────────────────────────────────────────────────────

export interface FieldIssue {
  path: string;
  message: string;
}

/** `{ error: { code, message, fields, recovery } }` from the API, or a transport failure. */
export class AuthorWithAiApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: FieldIssue[];
  readonly recovery: string[];

  constructor(status: number, code: string, message: string, fields: FieldIssue[] = [], recovery: string[] = []) {
    super(message);
    this.name = 'AuthorWithAiApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.recovery = recovery;
  }

  /** Network failures and 5xx answers may succeed on a retry with the same request identity. */
  get transient(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

const errorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    fields: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
    recovery: z.array(z.string()).optional(),
  }),
});

export async function parseAuthorWithAiError(response: Response): Promise<AuthorWithAiApiError> {
  const body: unknown = await response.json().catch(() => null);
  const parsed = errorBodySchema.safeParse(body);
  if (parsed.success) {
    const { code, message, fields, recovery } = parsed.data.error;
    return new AuthorWithAiApiError(response.status, code, message, fields ?? [], recovery ?? []);
  }
  return new AuthorWithAiApiError(response.status, `http_${response.status}`, response.statusText || 'Request failed');
}

const networkError = (error: unknown): AuthorWithAiApiError =>
  new AuthorWithAiApiError(0, 'network_error', error instanceof Error && error.message ? error.message : 'Network request failed');

const parseWith = <T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, value: unknown): T => {
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    throw new AuthorWithAiApiError(200, 'unexpected_response', 'The server answered in a format this page does not understand');
  }
  return parsed.data;
};

async function request<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, url: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetchWithAuthRetry(url, init, { json: 'withBody' });
  } catch (error) {
    throw networkError(error);
  }
  if (!response.ok) throw await parseAuthorWithAiError(response);
  return parseWith(schema, await response.json());
}

/**
 * Sends one command with a fresh request identity. A lost response (network
 * error or 5xx) is retried once with the same identity, so the server
 * replays the admitted result instead of admitting the command twice.
 */
async function sendCommand(path: string, body: Record<string, unknown>): Promise<AdmissionResponse> {
  const init: RequestInit = { method: 'POST', body: JSON.stringify({ requestId: crypto.randomUUID(), ...body }) };
  try {
    return await request(admissionResponseSchema, `${BASE}${path}`, init);
  } catch (error) {
    if (error instanceof AuthorWithAiApiError && error.transient) {
      return request(admissionResponseSchema, `${BASE}${path}`, init);
    }
    throw error;
  }
}

// ── Fetchers ─────────────────────────────────────────────────────────────────

/** null when the feature is not mounted on this server (404). */
export async function fetchAuthorWithAiDiagnostics(): Promise<AuthorWithAiDiagnostics | null> {
  try {
    return await request(diagnosticsSchema, `${BASE}/diagnostics`);
  } catch (error) {
    if (error instanceof AuthorWithAiApiError && error.status === 404) return null;
    throw error;
  }
}

/** The server derives the bank scope from the owned build, never from the query. */
export type BuildQuestionFilters = Omit<QuestionListFilters, 'externalAccountId' | 'includeArchived'>;

export async function fetchBuildQuestions(buildId: string, params: BuildQuestionFilters): Promise<QuestionListResult> {
  const search = buildQuestionSearchParams(params);
  let response: Response;
  try {
    response = await fetchWithAuthRetry(`${BASE}/builds/${encodeURIComponent(buildId)}/questions?${search.toString()}`);
  } catch (error) {
    throw networkError(error);
  }
  if (!response.ok) throw await parseAuthorWithAiError(response);
  return await response.json() as QuestionListResult;
}

/**
 * What the Question Bank picker builds its controls from, in the build's
 * scope: the concept vocabulary (autosuggest, browse-all), the taxonomy
 * tree ("Skills & risk areas") and the type and purpose pairs.
 */
export async function fetchBuildQuestionFacets(buildId: string): Promise<QuestionBrowseFacets> {
  let response: Response;
  try {
    response = await fetchWithAuthRetry(`${BASE}/builds/${encodeURIComponent(buildId)}/question-facets`);
  } catch (error) {
    throw networkError(error);
  }
  if (!response.ok) throw await parseAuthorWithAiError(response);
  return await response.json() as QuestionBrowseFacets;
}

export interface BuildListParams {
  accountId: number | null;
  statuses?: readonly BuildStatus[];
  cursor?: string | null;
  limit?: number;
}

export async function fetchBuildList(params: BuildListParams): Promise<BuildList> {
  const search = new URLSearchParams();
  if (params.accountId !== null) search.set('accountId', String(params.accountId));
  for (const status of params.statuses ?? []) search.append('status', status);
  if (params.cursor) search.set('cursor', params.cursor);
  if (params.limit) search.set('limit', String(params.limit));
  return request(buildListSchema, `${BASE}/builds?${search.toString()}`);
}

/** The API pages messages forward only; at most 200 per request. */
export const MESSAGE_PAGE_SIZE = 200;

export async function fetchBuildView(buildId: string, options: { messagesAfter?: number; messageLimit?: number } = {}): Promise<BuildView> {
  const search = new URLSearchParams();
  if (options.messagesAfter) search.set('messagesAfter', String(options.messagesAfter));
  search.set('messageLimit', String(options.messageLimit ?? MESSAGE_PAGE_SIZE));
  return request(buildViewSchema, `${BASE}/builds/${encodeURIComponent(buildId)}?${search.toString()}`);
}

export function createBuild(input: CreateBuildInput): Promise<AdmissionResponse> {
  return sendCommand('/builds', {
    accountId: input.accountId,
    source: input.source,
    ...(input.initialMessage ? { initialMessage: input.initialMessage } : {}),
    ...(input.document ? { document: input.document } : {}),
  });
}

const uploadResponseSchema = z.object({ fileId: z.string() });

/** Stores the original file for an "Upload a file" build; the bytes are the body, the API never opens them. */
export async function uploadDocument(file: File, contentType: string): Promise<{ fileId: string }> {
  let response: Response;
  try {
    response = await fetchWithAuthRetry(`${BASE}/uploads`, {
      method: 'POST',
      headers: { 'Content-Type': contentType, 'X-File-Name': encodeURIComponent(file.name) },
      body: file,
    }, { json: 'never' });
  } catch (error) {
    throw networkError(error);
  }
  if (!response.ok) throw await parseAuthorWithAiError(response);
  return parseWith(uploadResponseSchema, await response.json());
}

/** The original file of an uploaded_file build, served with the session cookie as a download. */
export const buildFileUrl = (buildId: string): string => `${BASE}/builds/${encodeURIComponent(buildId)}/file`;

export function postBuildMessage(buildId: string, text: string): Promise<AdmissionResponse> {
  return sendCommand(`/builds/${encodeURIComponent(buildId)}/messages`, { text });
}

export function postBuildAction(buildId: string, action: BuildAction): Promise<AdmissionResponse> {
  return sendCommand(`/builds/${encodeURIComponent(buildId)}/actions`, { action });
}
